import { useLayoutEffect, useState, type RefObject } from "react";

/** Fit the index around the actual sticky heading and the unchanged Moo launcher. */
export function usePayeeAlphabetLayout(nav: RefObject<HTMLElement | null>) {
  const [layout, setLayout] = useState({ top: 0, left: 0, height: 324 });
  useLayoutEffect(() => {
    const directory = nav.current?.closest(".payee-directory");
    const anchor = directory?.querySelector(".payee-directory-body");
    const heading = document.querySelector(".secondary-heading");
    const launcher = document.querySelector(".moo-launcher");
    const navigation = document.querySelector(".bottom-nav");
    if (!anchor || !heading) return;
    let frame = 0;
    const measure = () => {
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop ?? 0;
      const viewportBottom = viewportTop + (viewport?.height ?? window.innerHeight);
      const minimumTop = Math.max(viewportTop, heading.getBoundingClientRect().bottom) + 8;
      const clearance = (parseFloat(getComputedStyle(heading).top) || 0) + heading.getBoundingClientRect().height + 8;
      if (directory instanceof HTMLElement) directory.style.setProperty("--payee-heading-clearance", `${clearance}px`);
      // Opening Moo replaces its launcher; re-read it when the shell changes.
      const moo = document.querySelector(".moo-launcher")?.getBoundingClientRect();
      const navigationBounds = navigation?.getBoundingClientRect();
      const bottom = Math.min(viewportBottom, moo?.height ? moo.top : viewportBottom,
        navigationBounds?.height ? navigationBounds.top : viewportBottom) - 12;
      const anchorBounds = anchor.getBoundingClientRect();
      const top = Math.max(minimumTop, Math.min(anchorBounds.top, bottom - 324));
      const left = anchorBounds.right - 20;
      const height = Math.max(0, Math.min(324, bottom - top));
      setLayout(previous => previous.top === top && previous.left === left && previous.height === height ? previous : { top, left, height });
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = new ResizeObserver(schedule);
    for (const element of [anchor, heading, launcher]) if (element) observer.observe(element);
    const shell = directory?.closest(".app-shell");
    const shellObserver = new MutationObserver(schedule);
    if (shell) shellObserver.observe(shell, { childList: true });
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("scroll", schedule);
    measure();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      shellObserver.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("scroll", schedule);
    };
  }, [nav]);
  return layout;
}
